import { supabase } from "@/integrations/supabase/client";
import {
  actorFromProfile,
  DOCUMENT_HISTORY_SOURCE_BATCH_SIZE,
  resolveSnapshotAt,
  resolveWatermark,
  type DocumentHistoryScope,
  type DocumentHistorySourceCursor,
  type HistoryProfileRow,
} from "@/domains/documents/history/documentHistoryShared";

interface CreatedDocumentRow {
  id: number;
  filename: string;
  file_path: string | null;
  content_type: string | null;
  size: number | null;
  state: string | null;
  created_at: string;
  created_by: number;
  is_deleted: boolean | null;
  version_group: string | null;
  version: number | null;
  actor: HistoryProfileRow | null;
}

interface LineageRow {
  id: number;
  version_group: string | null;
  version: number | null;
}

const CREATED_COLUMNS =
  "id, filename, file_path, content_type, size, state, created_at, created_by, " +
  "is_deleted, version_group, version, " +
  "actor:user_profiles!documents_created_by_fk(id, first_name, last_name, email, avatar_url, color)";

/**
 * Rows per page of the lineage read. PostgREST truncates an unbounded response
 * at `max_rows` (1000 here, as `useTemplateFormDocuments` documents) with no
 * error and no way to tell a complete result from a clipped one — and one batch
 * can carry 50 trashed version groups, so twenty versions apiece already reaches
 * the cap. A clipped read would hand back the wrong head for the groups it lost,
 * and the filter below would then drop EVERY trashed row of those lineages: the
 * exact "deleted a document that was never added" regression this source exists
 * to close, failing silently. So it pages, ordered, the way the audit reads in
 * `repository/mutations.ts` do.
 */
const LINEAGE_PAGE_SIZE = 1000;

/**
 * The documents this history is about: one building, optionally one project, no
 * hidden rows and no generated DWG child PDFs (a child is not a file a user ever
 * sees, and it would contribute an "Added" entry of its own).
 */
function scopedDocuments(scope: DocumentHistoryScope, columns: string) {
  const query = supabase
    .from("documents")
    .select(columns)
    .eq("org_id", scope.orgId)
    .eq("building_id", scope.buildingId)
    .eq("hidden", false)
    .or("is_dwg_child.is.null,is_dwg_child.eq.false");

  return scope.projectId === null ? query : query.eq("project_id", scope.projectId);
}

/** Later version wins; equal (or absent) versions fall back to the newer row. */
function outranks(row: LineageRow, best: LineageRow): boolean {
  const version = row.version ?? 0;
  const bestVersion = best.version ?? 0;
  return version === bestVersion ? row.id > best.id : version > bestVersion;
}

/** Every row of the given lineages, in pages, so nothing is silently clipped. */
async function fetchLineageRows(
  scope: DocumentHistoryScope,
  versionGroups: string[]
): Promise<LineageRow[]> {
  const rows: LineageRow[] = [];
  for (let offset = 0; ; offset += LINEAGE_PAGE_SIZE) {
    const { data, error } = await scopedDocuments(scope, "id, version_group, version")
      .in("version_group", versionGroups)
      // Ordered so the pages address a stable result set.
      .order("id", { ascending: true })
      .range(offset, offset + LINEAGE_PAGE_SIZE - 1);
    if (error) throw error;

    const batch = (data as unknown as LineageRow[]) ?? [];
    rows.push(...batch);
    if (batch.length < LINEAGE_PAGE_SIZE) break;
  }
  return rows;
}

/** The highest-ranking row of each lineage: its head at delete time. */
function electHeads(rows: LineageRow[]): Map<string, LineageRow> {
  const heads = new Map<string, LineageRow>();
  for (const row of rows) {
    if (!row.version_group) continue;
    const best = heads.get(row.version_group);
    if (!best || outranks(row, best)) heads.set(row.version_group, row);
  }
  return heads;
}

/**
 * Drop the superseded versions of a lineage, keeping the row the document lists
 * showed — for trashed rows too.
 *
 * `is_latest_version` cannot answer this on its own: the flag is re-elected on
 * every `is_deleted` change (see `documentHistoryScopedDocuments`), so a trashed
 * document is no longer its lineage's head. Matching it in the query dropped the
 * "Added to Documents" entry of every document sitting in the Trash while its
 * "Moved to Trash" entry — read from immutable `event_logs` — stayed, so history
 * showed a file deleted that it never showed being added, and the missing entry
 * reappeared only after a restore.
 *
 * So the query keeps live heads plus every trashed row, and the trashed ones are
 * resolved here against their lineage: the head at delete time is the highest
 * version in the group, which is the row `moveToTrash` logged the delete for.
 * Resolved in scope, so a copy that carried its version group into another
 * project cannot decide which row this building's history shows.
 */
async function keepLineageHeads(
  scope: DocumentHistoryScope,
  rows: CreatedDocumentRow[]
): Promise<CreatedDocumentRow[]> {
  const trashedGroups = [
    ...new Set(
      rows.flatMap((row) => (row.is_deleted && row.version_group ? [row.version_group] : []))
    ),
  ];
  // A live row got here by being its lineage's head, and a trashed row with no
  // version group is a lineage of one.
  if (trashedGroups.length === 0) return rows;

  const heads = electHeads(await fetchLineageRows(scope, trashedGroups));

  return rows.filter(
    (row) => !row.is_deleted || !row.version_group || heads.get(row.version_group)?.id === row.id
  );
}

export async function fetchCreatedDocuments(
  scope: DocumentHistoryScope,
  cursor: DocumentHistorySourceCursor
): Promise<DocumentHistorySourceCursor> {
  let query = scopedDocuments(scope, CREATED_COLUMNS)
    // One entry per lineage, as the document lists show — a re-uploaded file
    // must not contribute an "added" entry per superseded version. Live rows are
    // narrowed to the elected head here; trashed rows carry no usable head flag
    // and are resolved by `keepLineageHeads` instead.
    .or("is_latest_version.eq.true,is_deleted.eq.true")
    .not("created_at", "is", null)
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .range(cursor.offset, cursor.offset + DOCUMENT_HISTORY_SOURCE_BATCH_SIZE - 1);

  if (cursor.snapshotAt !== null) {
    query = query.lte("created_at", cursor.snapshotAt);
  }

  const { data, error } = await query;
  if (error) throw error;

  const rows = (data as unknown as CreatedDocumentRow[]) ?? [];
  const heads = await keepLineageHeads(scope, rows);

  return {
    // Offset, exhaustion and watermark come off the raw batch, exactly as the
    // audit source does it: superseded versions were still read, and that is
    // what proves how far back this source has got.
    offset: cursor.offset + rows.length,
    done: rows.length < DOCUMENT_HISTORY_SOURCE_BATCH_SIZE,
    snapshotAt: resolveSnapshotAt(cursor, rows),
    watermark: resolveWatermark(cursor, rows),
    buffered: heads.map((row) => ({
      id: `document-${row.id}`,
      documentId: String(row.id),
      displayName: null,
      filename: row.filename,
      filePath: row.file_path,
      contentType: row.content_type,
      size: row.size,
      version: row.version,
      versionGroup: row.version_group,
      isDeleted: row.is_deleted === true,
      state: row.state,
      action: "added",
      createdAt: row.created_at,
      actor: actorFromProfile(row.actor, row.created_by),
    })),
  };
}
